import React, { useState, useEffect } from "react";
import { useCart } from "../shared/CartContext";
import { supabase } from "../lib/supabaseclient";

export default function Checkout() {
  const { items, totals, clear } = useCart();
  const cartItems = Array.from(items.values());

  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    country: "",
    city: "",
  });

  const [citiesForSelected, setCitiesForSelected] = useState([]);
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // 🔹 Fetch countries (and their cities)
  useEffect(() => {
    let active = true;
    setLoadingCountries(true);

    fetch("https://countriesnow.space/api/v0.1/countries")
      .then((res) => {
        if (!res.ok) throw new Error(`countriesnow HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const list = data?.data || [];
        const formatted = list
          .map((c) => ({
            name: c.country,
            cities: c.cities || [],
          }))
          .filter((c) => c.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        if (active) setCountries(formatted);
      })
      .catch(() => {
        fetch("https://restcountries.com/v3.1/all")
          .then((r) => r.json())
          .then((data) => {
            const formatted = data
              .map((c) => ({
                name: c.name.common,
                cities: [],
              }))
              .sort((a, b) => a.name.localeCompare(b.name));
            if (active) setCountries(formatted);
          });
      })
      .finally(() => {
        if (active) setLoadingCountries(false);
      });

    return () => {
      active = false;
    };
  }, []);

  // 🔹 Update cities when country changes
  useEffect(() => {
    if (!form.country) {
      setCitiesForSelected([]);
      return;
    }
    const match = countries.find((c) => c.name === form.country);
    setCitiesForSelected(match ? match.cities : []);
    setForm((prev) => ({ ...prev, city: "" }));
  }, [form.country, countries]);

  // 🔹 Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // 🔹 Validation
  const validate = () => {
    const err = {};
    if (!form.name.trim()) err.name = "Full name is required.";
    if (!form.email.trim()) err.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      err.email = "Enter a valid email.";
    if (!form.phone.trim()) err.phone = "Phone is required.";
    if (!form.address.trim()) err.address = "Address is required.";
    if (!form.country.trim()) err.country = "Country is required.";
    if (!form.city.trim()) err.city = "City is required.";
    return err;
  };

  // 🔹 Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("⏳ Form submitted");
  
    const err = validate();
    setErrors(err);
    if (Object.keys(err).length > 0) {
      console.log("⚠️ Validation failed:", err);
      return;
    }
  
    setSubmitting(true);
    console.log("⏳ Submitting started");
  
    const orderData = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      address: form.address,
      country: form.country,
      city: form.city,
      total_amount: totals?.subtotal ?? 0,
      items: cartItems.map((it) => ({
        title: it.title,
        subtitle: it.subtitle || null,
        quantity: it.quantity,
        price: it.price,
      })),
    };
  
    const { error } = await supabase.from("orders").insert([orderData]);
  
    if (error) {
      console.error("❌ Insert failed:", error.message);
      setSubmitting(false);
      return;
    }
  
    try {
      const emailRes = await fetch("https://btdhzueiqamljnbfgnob.supabase.co/functions/v1/send-email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`, // ✅ include anon key
        },
        body: JSON.stringify(orderData),
      });
      console.log("📩 Email response:", emailRes.status);
  
      if (!emailRes.ok) {
        const text = await emailRes.text();
        console.error("❌ Email send failed:", text);
      } else {
        console.log("✅ Email notification sent successfully!");
      }
    } catch (err) {
      console.error("❌ Error sending email:", err);
    }
  
    console.log("✅ Submitting finished");
  
    setShowSuccess(orderData);
    clear();
    setForm({
      name: "",
      email: "",
      phone: "",
      address: "",
      country: "",
      city: "",
    });
    setCitiesForSelected([]);
    setSubmitting(false);
  };
  
  

  return (
    <div className="max-w-3xl mx-auto px-6 py-12 relative">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">Checkout</h2>

      {/* 🛒 Cart summary */}
      <div className="bg-white border rounded-xl divide-y shadow-sm">
        {cartItems.length === 0 ? (
          <div className="p-6 text-gray-600">No items in cart.</div>
        ) : (
          cartItems.map((it, i) => (
            <div key={i} className="p-6 flex items-center justify-between">
              <div>
                <div className="font-semibold text-gray-900">{it.title}</div>
                {it.subtitle && (
                  <div className="text-sm text-gray-600">{it.subtitle}</div>
                )}
              </div>
              <div className="text-gray-900">
                {it.quantity} × Rs {Number(it.price).toFixed(2)}
              </div>
            </div>
          ))
        )}
      </div>

      {cartItems.length > 0 && (
        <>
          {/* 💳 Totals */}
          <div className="mt-6 bg-white border rounded-xl p-6 shadow-sm">
            <div className="flex justify-between text-sm text-gray-700">
              <span>Subtotal</span>
              <span>Rs {totals?.subtotal?.toFixed(2) ?? "0.00"}</span>
            </div>
            <div className="flex justify-between font-semibold text-gray-900 mt-2">
              <span>Total</span>
              <span>Rs {totals?.subtotal?.toFixed(2) ?? "0.00"}</span>
            </div>
          </div>

          {/* 👤 Form */}
          <form
            onSubmit={handleSubmit}
            className="mt-8 bg-white border rounded-xl p-6 shadow-sm space-y-4"
            noValidate
          >
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              Customer Information
            </h3>

            {/* Name */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Full Name
              </label>
              <input
                name="name"
                value={form.name}
                onChange={handleChange}
                className={`w-full border rounded-lg px-4 py-2 outline-none focus:ring-2 ${
                  errors.name ? "ring-red-400" : "focus:ring-blue-500"
                }`}
              />
              {errors.name && (
                <div className="text-sm text-red-600 mt-1">{errors.name}</div>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">Email</label>
              <input
                name="email"
                value={form.email}
                onChange={handleChange}
                type="email"
                className={`w-full border rounded-lg px-4 py-2 outline-none focus:ring-2 ${
                  errors.email ? "ring-red-400" : "focus:ring-blue-500"
                }`}
              />
              {errors.email && (
                <div className="text-sm text-red-600 mt-1">{errors.email}</div>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">Phone</label>
              <input
                name="phone"
                value={form.phone}
                onChange={handleChange}
                type="tel"
                className={`w-full border rounded-lg px-4 py-2 outline-none focus:ring-2 ${
                  errors.phone ? "ring-red-400" : "focus:ring-blue-500"
                }`}
              />
              {errors.phone && (
                <div className="text-sm text-red-600 mt-1">{errors.phone}</div>
              )}
            </div>

            {/* Address */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Address
              </label>
              <textarea
                name="address"
                value={form.address}
                onChange={handleChange}
                rows="3"
                className={`w-full border rounded-lg px-4 py-2 outline-none focus:ring-2 ${
                  errors.address ? "ring-red-400" : "focus:ring-blue-500"
                }`}
              />
              {errors.address && (
                <div className="text-sm text-red-600 mt-1">
                  {errors.address}
                </div>
              )}
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm text-gray-600 mb-1">
                Country
              </label>
              <select
                name="country"
                value={form.country}
                onChange={handleChange}
                disabled={loadingCountries}
                className={`w-full border rounded-lg px-4 py-2 bg-white outline-none focus:ring-2 ${
                  errors.country ? "ring-red-400" : "focus:ring-blue-500"
                }`}
              >
                <option value="">
                  {loadingCountries ? "Loading countries..." : "Select Country"}
                </option>
                {countries.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
              {errors.country && (
                <div className="text-sm text-red-600 mt-1">
                  {errors.country}
                </div>
              )}
            </div>

            {/* City */}
            {form.country && (
              <div>
                <label className="block text-sm text-gray-600 mb-1">City</label>
                {citiesForSelected.length > 0 ? (
                  <select
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    className={`w-full border rounded-lg px-4 py-2 bg-white outline-none focus:ring-2 ${
                      errors.city ? "ring-red-400" : "focus:ring-blue-500"
                    }`}
                  >
                    <option value="">Select City</option>
                    {citiesForSelected.map((city) => (
                      <option key={city} value={city}>
                        {city}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    placeholder="Enter your city"
                    className={`w-full border rounded-lg px-4 py-2 outline-none focus:ring-2 ${
                      errors.city ? "ring-red-400" : "focus:ring-blue-500"
                    }`}
                  />
                )}
                {errors.city && (
                  <div className="text-sm text-red-600 mt-1">{errors.city}</div>
                )}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting || loadingCountries}
              className={`w-full py-3 rounded-lg font-semibold text-white transition-all ${
                submitting || loadingCountries
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-cyan-500 hover:opacity-90"
              }`}
            >
              {submitting ? "Placing order..." : "Place Order"}
            </button>
          </form>
        </>
      )}

      {/* ✅ Simplified Success Modal */}
      {/* ✅ Simplified Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl text-center">
            <h3 className="text-2xl font-semibold text-green-600 mb-3">
              🎉 Order Placed Successfully
            </h3>

            <p className="text-gray-700 mb-2">
              Thank you, <b>{showSuccess.name}</b>!
            </p>

            <p className="text-gray-700 mb-4">
              Our team will contact you soon at <b>{showSuccess.email}</b>
              {showSuccess.phone && (
                <>
                  {" "}
                  or <b>{showSuccess.phone}</b>
                </>
              )}{" "}
              for confirmation.
            </p>

            <div className="bg-gray-50 p-4 rounded-lg mb-4 text-gray-800 font-medium">
              Total Payment: Rs {showSuccess.total_amount.toFixed(2)}
            </div>

            <button
              onClick={() => setShowSuccess(null)}
              className="px-6 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
